import { LegacyRef, Ref, RefObject } from 'react';

import { SubscriptionState } from 'src/types/subscription';
import { mixpanelObject } from 'src/utils/analytics';
import { putFacebookEvent } from 'src/utils/analytics/fbpixel/fbCapiHelper';
import { gtm } from 'src/utils/analytics/gtm';
import {
  MIXPANEL_COUNTER,
  MIXPANEL_UNIQUE_ID
} from 'src/utils/analytics/mixpanel/mixpanelConstants';
import { packToggleEvent } from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import { getLocalStorage } from 'src/utils/browserStorageHandler';
import { ActivePromotions } from 'src/utils/commonTypes';
import { GUEST_TOKEN } from 'src/utils/constants/localStorageConstants';
import { META_EVENTS } from 'src/utils/constants/metaPixelConstants';
import {
  FeatureFlagConstants,
  REMOTE_CONFIG
} from 'src/utils/constants/utilConstants';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';
import { fbqTrack } from 'src/utils/scripts/thirdPartyScripts';
import {
  calculateDisplayPriceNew,
  calculateSellPriceNew,
  checkIsAtBestPlan,
  getCurrencySymbol,
  getExpiryDate,
  trackSelectedPlanMixpanelEvents,
  transformNewPlanToOldPlan
} from 'src/utils/SubscriptionHelper/SubscriptionHelper';
import { generateUUID } from 'src/utils/UtilityMethods';

import {
  Badge,
  CardContainer,
  Container,
  lavendarTag,
  grayTag,
  goldTag,
  Subtitle1,
  Subtitle2,
  TagContainer,
  TextContainer,
  Title,
  ValidTimeText,
  greyContainer,
  lavendarContainer,
  goldContainer,
  noBorderTop,
  transparentBadge,
  BestPlanContainer,
  BestPlanPrice,
  BestPlanTitle,
  roundedBorder
} from './style';
import { useAppStore } from '../../../../hooks/useAppStore';
import {
  SUBSCRIPTION_ACTIONS,
  useSubscriptionDispatch,
  useSubscriptionState
} from '../../SubscriptionContext';

enum TagVariantsEnum {
  GRAY,
  LAVENDER,
  GOLD,
  GREEN
}

const Tag = ({
  variant,
  show,
  text
}: {
  show: boolean;
  variant?: TagVariantsEnum;
  text?: string;
}) => {
  // prevent shifting other cards
  if (!show) return <TagContainer>&nbsp;</TagContainer>;

  let tagStyle = lavendarTag;
  switch (variant) {
    case TagVariantsEnum.LAVENDER:
      tagStyle = lavendarTag;
      break;
    case TagVariantsEnum.GOLD:
      tagStyle = goldTag;
      break;
    case TagVariantsEnum.GRAY:
      tagStyle = grayTag;
      break;
  }
  return <TagContainer css={tagStyle}>{text}</TagContainer>;
};

const getContainerStyle = (active: boolean, isPremium: boolean) => {
  if (!active) return greyContainer;
  if (isPremium) return goldContainer;
  return lavendarContainer;
};

const getTagVariant = (label: string, isPremium = false, isCurrent = false) => {
  if (isPremium) return TagVariantsEnum.GOLD;
  if (isCurrent) return TagVariantsEnum.GRAY;
  if (label === 'CMS_PLAN_BEST_VALUE_TEXT') return TagVariantsEnum.GREEN;
  if (label === 'CMS_PLAN_RECOMMENDED_TEXT') return TagVariantsEnum.LAVENDER;
};

const BestPlanCard = ({
  title,
  subTitle,
  validityText,
  id
}: {
  title: string;
  subTitle: string;
  validityText: string;
  id: string;
}) => {
  return (
    <CardContainer>
      <BestPlanContainer
        data-test-id={`best-plan_${id}`}
        css={[goldContainer, roundedBorder]}>
        <BestPlanPrice data-test-id={`best-plan-price`}>
          {subTitle}
        </BestPlanPrice>
        <BestPlanTitle data-test-id={`best-plan-title`}>{title}</BestPlanTitle>
        <ValidTimeText data-test-id={`best-plan-validity`}>
          {validityText}
        </ValidTimeText>
      </BestPlanContainer>
    </CardContainer>
  );
};

const PlanCard = ({
  data,
  packToggleCounter,
  activePromotions = {},
  planRef
}: {
  data: SubscriptionState;
  packToggleCounter: { current: number };
  activePromotions: ActivePromotions;
  planRef?: RefObject<HTMLDivElement>;
}) => {
  const {
    translations = {},
    subscriptionData,
    isUserLoggedIn,
    usersData,
    remoteConfig
  } = useAppStore();
  const subscriptionState = useSubscriptionState();
  const subscriptionStrings = translations.subscription ?? {};
  const { options, active, id, isPremium = false, selectedLabel } = data;
  const activeOption = options.find((option) => option.active);
  const {
    price,
    purchaseAllowed,
    title: optionTitle = ''
  } = activeOption ?? {};
  const {
    discountPercentage = 0,
    market,
    currency = 'INR',
    label = ''
  } = price ?? {};
  const showBadge = discountPercentage > 0;
  const currencySymbol = getCurrencySymbol(currency);
  const subTitle1 = `${currencySymbol} ${
    activeOption ? calculateSellPriceNew(activeOption, activePromotions) : null
  }/-`;
  const subTitle2 = `${currencySymbol} ${
    activeOption
      ? calculateDisplayPriceNew(activeOption, activePromotions)
      : null
  }`;
  const containerStyle = getContainerStyle(active, isPremium);

  const badgeText = subscriptionStrings[label]?.replace(
    '{{discount_value_computed}}',
    `${discountPercentage}`
  );
  const isCurrent =
    subscriptionData?.maxPricePack?.subscription_plan?.id === activeOption?.id;
  const tagText =
    subscriptionStrings[isCurrent ? 'CMS_CURRENTPLAN_TEXT' : selectedLabel] ??
    '';
  const showTag = (active || isCurrent) && tagText;
  const dispatch = useSubscriptionDispatch();

  const triggerPackToggleEvents = () => {
    mixpanelObject.setProperty(MIXPANEL_COUNTER, packToggleCounter.current);
    mixpanelObject.setProperty(
      MIXPANEL_UNIQUE_ID,
      isUserLoggedIn ? usersData?.id : getLocalStorage(GUEST_TOKEN)
    );
    packToggleEvent();
    trackSelectedPlanMixpanelEvents(activeOption, !!subscriptionData);
  };

  const handlePlanClick = (planCategory: SubscriptionState) => () => {
    if (!purchaseAllowed) {
      return;
    }
    triggerPackToggleEvents();
    if (
      isFeatureEnabled(
        remoteConfig,
        FeatureFlagConstants.FEATURE_META_PIXEL_INTEGRATION
      )
    ) {
      const eventId = generateUUID();
      fbqTrack(META_EVENTS.Addtocart, eventId);
      putFacebookEvent(
        META_EVENTS.Addtocart,
        new Date().getTime(),
        window.location.href,
        eventId
      );
    }
    const plan = planCategory?.options.find((item) => item.active);
    if (plan) {
      gtm.packSelectedEventGTM(transformNewPlanToOldPlan(plan));
    }
    dispatch({
      type: SUBSCRIPTION_ACTIONS.UPDATE_CATEGORY,
      payload: { id: planCategory?.id }
    });
  };

  const isAtBestPlan =
    subscriptionData?.maxPricePack?.id && checkIsAtBestPlan(subscriptionState);

  if (isAtBestPlan) {
    const expiryDate = getExpiryDate(
      subscriptionData?.maxPricePack?.subscription_end
    );
    const validityText = `${subscriptionStrings.ACTIVE_TILL} ${expiryDate}`;

    return (
      <BestPlanCard
        title={optionTitle}
        subTitle={subTitle1}
        id={id}
        validityText={validityText}
      />
    );
  }

  return (
    <CardContainer
      ref={planRef}
      data-test-id={`plan-category${active ? '-active' : ''}`}>
      <Tag
        data-test-id={`tag_${id}`}
        show={showTag}
        variant={getTagVariant(selectedLabel, isPremium, isCurrent)}
        text={tagText}
      />
      <Container
        data-test-id={`plan-card_${id}`}
        css={[containerStyle, showTag ? noBorderTop : null]}
        onClick={handlePlanClick(data)}>
        <Badge
          data-test-id={`offer_${id}`}
          css={[!showBadge && transparentBadge]}>
          {showBadge ? badgeText : ''} &nbsp;
        </Badge>
        <TextContainer>
          {market && subTitle2 !== subTitle1 ? (
            <Subtitle2 data-test-id={`discounted-price_${id}`}>
              {subTitle2}
            </Subtitle2>
          ) : null}
          <Subtitle1 data-test-id={`price_${id}`}>{subTitle1}</Subtitle1>
          <Title data-test-id={`plan-title_${id}`}>{optionTitle}</Title>
        </TextContainer>
      </Container>
    </CardContainer>
  );
};

export default PlanCard;
